<template>
  <div class="table-container table-container--hide-checkbox-mobile">
    <div class="table-heading">
      <div class="table-heading__title">
        {{ $t('submissions.all_submissions') }}
        <span class="table-heading__count">{{ total }}</span>
      </div>
      <div class="table-heading__actions">
        <!-- Status Filter -->
        <DropdownMenu class="status-filter">
          <template #trigger>
            <Btn
              variant="tertiary"
              icon="filter"
              :class="{ 'filter-active': hasActiveStatusFilter }"
            >
              {{ $t('submissions.filter.status') }}
              <span v-if="hasActiveStatusFilter" class="filter-count">
                {{ selectedStatuses.length }}
              </span>
            </Btn>
          </template>
          <template #items>
            <div class="status-filter__header">
              <span>{{ $t('submissions.filter.filter_by_status') }}</span>
              <button class="status-filter__reset" @click="resetStatusFilter">
                {{ $t('submissions.filter.reset') }}
              </button>
            </div>
            <label v-for="status in allStatuses" :key="status.value" class="status-filter__option">
              <input
                type="checkbox"
                :value="status.value"
                v-model="selectedStatuses"
                @change="applyFilters"
              />
              <span
                class="submission-status"
                :class="{ ['submission-status--' + status.value]: status.value }"
              >
                {{ status.label }}
              </span>
            </label>
          </template>
        </DropdownMenu>

        <!-- Form Filter - hidden on mobile -->
        <DropdownMenu class="form-filter hide-on-mobile">
          <template #trigger>
            <Btn
              variant="tertiary"
              icon="file-text"
              :class="{ 'filter-active': hasActiveFormFilter }"
            >
              {{ $t('submissions.filter.form') }}
              <span v-if="hasActiveFormFilter" class="filter-count">
                {{ selectedForms.length }}
              </span>
            </Btn>
          </template>
          <template #items>
            <div class="status-filter__header">
              <span>{{ $t('submissions.filter.filter_by_form') }}</span>
              <button class="status-filter__reset" @click="resetFormFilter">
                {{ $t('submissions.filter.reset') }}
              </button>
            </div>
            <label v-for="form in forms" :key="form.id" class="status-filter__option">
              <input
                type="checkbox"
                :value="form.id"
                v-model="selectedForms"
                @change="applyFilters"
              />
              <span class="form-name">{{ form.name }}</span>
            </label>
            <div v-if="forms.length === 0" class="status-filter__empty">
              {{ $t('submissions.filter.all_forms') }}
            </div>
          </template>
        </DropdownMenu>

        <!-- Export button - hidden on mobile -->
        <Btn
          class="hide-on-mobile"
          variant="tertiary"
          size="sm"
          icon="export"
          :text="isExporting ? $t('submissions.exporting') : $t('submissions.export_to_csv')"
          @click="$emit('export')"
          :loading="isExporting"
        ></Btn>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div class="submissions-mobile-list">
      <div
        v-for="submission in formattedSubmissions"
        :key="submission.id"
        class="submission-card"
        @click="handleRowClick(submission)"
      >
        <div class="submission-card__left">
          <div class="submission-card__primary">
            <BaseIcon
              v-if="submission.status === 'partial'"
              name="draw"
              size="14"
              class="submission-card__icon submission-card__icon--partial"
            />
            <div v-else class="submission-card__dot"></div>
            <span class="submission-card__form">{{ submission.formName }}</span>
          </div>
          <div class="submission-card__secondary">
            <span class="submission-card__date">{{ submission.updatedAt }}</span>
          </div>
        </div>
        <div class="submission-card__right">
          <div
            class="submission-status submission-status--small"
            :class="{ ['submission-status--' + submission.status]: submission.status }"
          >
            {{
              submission.status === 'partial'
                ? $t('submissions.status.partial')
                : $t('submissions.status.complete')
            }}
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Table View -->
    <EasyDataTable
      class="submissions-desktop-table"
      :headers="headers"
      :items="formattedSubmissions"
      :loading="loading"
      :server-items-length="total"
      v-model:server-options="serverOptions"
      buttons-pagination
      @click-row="handleRowClick"
      table-class-name="custom-table"
    >
      <template #item-updatedAt="{ id, formId, updatedAt, status }">
        <TableRowLink :to="submissionRoute({ id, formId })" :aria-label="`Submission ${updatedAt}`">
          <span class="table-value">
            <BaseIcon v-if="status === 'partial'" name="draw" size="16" />
            <span v-else class="table-dot"></span>
            {{ updatedAt }}
          </span>
        </TableRowLink>
      </template>
    </EasyDataTable>
  </div>
</template>

<script>
  import { ref, watch, computed, getCurrentInstance, onMounted } from 'vue';
  import { formatDateTimeEU } from '@/utils/date';
  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';

  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';

  export default {
    components: {
      EasyDataTable,
      BaseIcon,
      Btn,
      DropdownMenu,
      TableRowLink,
    },

    props: {
      submissions: {
        type: Array,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      isExporting: {
        type: Boolean,
        default: false,
      },
      total: {
        type: Number,
        required: true,
      },
      currentPage: {
        type: Number,
        required: true,
      },
      pageSize: {
        type: Number,
        required: true,
      },
      initialStatus: {
        type: String,
        default: 'received',
      },
      forms: {
        type: Array,
        default: () => [],
      },
      initialFormIds: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['row-click', 'export', 'page-change', 'filter-change'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      // Status filter state
      const allStatuses = computed(() => [
        { value: 'received', label: $t('submissions.status.complete') || 'Complete' },
        { value: 'partial', label: $t('submissions.status.partial') || 'Draft' },
      ]);

      const defaultStatuses = ['received'];
      const selectedStatuses = ref([...defaultStatuses]);

      // Form filter state
      const selectedForms = ref([...props.initialFormIds]);

      const serverOptions = ref({
        page: props.currentPage,
        rowsPerPage: props.pageSize,
      });

      // Watch for changes in initialStatus prop and sync selectedStatuses
      watch(
        () => props.initialStatus,
        (newStatus) => {
          if (newStatus) {
            selectedStatuses.value = [newStatus];
          } else {
            selectedStatuses.value = allStatuses.value.map((s) => s.value);
          }
        },
        { immediate: true }
      );

      // Watch for changes in initialFormIds
      watch(
        () => props.initialFormIds,
        (newFormIds) => {
          selectedForms.value = [...newFormIds];
        },
        { immediate: true }
      );

      // Computed: check if filters are active
      const hasActiveStatusFilter = computed(() => {
        return selectedStatuses.value.length !== allStatuses.value.length;
      });

      const hasActiveFormFilter = computed(() => {
        return selectedForms.value.length > 0 && selectedForms.value.length !== props.forms.length;
      });

      // Apply filters
      const applyFilters = () => {
        emit('filter-change', {
          statuses: selectedStatuses.value,
          formIds: selectedForms.value,
        });
      };

      const resetStatusFilter = () => {
        selectedStatuses.value = allStatuses.value.map((s) => s.value);
        applyFilters();
      };

      const resetFormFilter = () => {
        selectedForms.value = [];
        applyFilters();
      };

      // Watch for changes in serverOptions and emit page-change event
      watch(
        serverOptions,
        (newOptions) => {
          emit('page-change', {
            page: newOptions.page,
            limit: newOptions.rowsPerPage,
          });
        },
        { deep: true }
      );

      const headers = [
        { text: $t('common.updated_at'), value: 'updatedAt' },
        { text: $t('submissions.form'), value: 'formName' },
      ];

      const formattedSubmissions = computed(() => {
        return props.submissions.map((submission) => {
          const status = submission.is_complete ? 'complete' : 'partial';
          return {
            ...submission,
            formId: submission.form?.id || submission.forms?.id || submission.form_id,
            formName: submission.form?.name || submission.forms?.name || 'Unknown Form',
            status: status,
            updatedAt: formatDateTimeEU(submission.updated_at),
          };
        });
      });

      const handleRowClick = (submission, event) => {
        instance.emit('row-click', submission, event);
      };

      const submissionRoute = (submission) => {
        if (submission.formId) {
          return `/flow/${submission.formId}/submissions/${submission.id}`;
        }
        return `/submissions/${submission.id}`;
      };

      return {
        headers,
        formattedSubmissions,
        handleRowClick,
        submissionRoute,
        serverOptions,
        // Status filter
        allStatuses,
        selectedStatuses,
        hasActiveStatusFilter,
        resetStatusFilter,
        // Form filter
        selectedForms,
        hasActiveFormFilter,
        resetFormFilter,
        // Apply filters
        applyFilters,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/_mixins.scss';

  // Hide on mobile
  .hide-on-mobile {
    @include lg-down {
      display: none !important;
    }
  }

  .icon {
    color: var(--slate-500);
  }

  .table-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background-color: var(--green-500);
    flex-shrink: 0;
    margin-right: 0.5rem;
  }

  .table-value {
    display: flex;
    align-items: center;
  }

  // Mobile card list - only visible on mobile
  .submissions-mobile-list {
    display: none;

    @include md-down {
      display: block;
    }
  }

  // Desktop table - hidden on mobile
  .submissions-desktop-table {
    @include md-down {
      display: none;
    }
  }

  // Mobile card styles
  .submission-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: var(--white);
    border-bottom: 1px solid var(--slate-200);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--slate-50);
    }

    &:last-child {
      border-bottom: none;
    }

    &__left {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
      flex: 1;
    }

    &__right {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-left: 1rem;
    }

    &__primary {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__dot {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background-color: var(--green-500);
      flex-shrink: 0;
    }

    &__icon {
      flex-shrink: 0;
      color: var(--slate-500);

      &--partial {
        color: var(--amber-500);
      }
    }

    &__form {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__secondary {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-500);
      padding-left: 1rem;
    }

    &__date {
      font-size: 0.8125rem;
    }
  }

  // Small status badge for mobile
  .submission-status--small {
    padding: 2px 6px;
    font-size: 11px;
  }

  // Filter dropdown styling (matching ProposalsView)
  .status-filter,
  .form-filter {
    .filter-active {
      background-color: var(--color-primary-50);
      border-color: var(--color-primary-200);
    }

    .filter-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      margin-left: 6px;
      font-size: 11px;
      font-weight: 600;
      color: var(--color-primary-700);
      background-color: var(--color-primary-100);
      border-radius: 9px;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 4px 8px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: 600;
      color: var(--slate-600);
      border-bottom: 1px solid var(--slate-100);
    }

    &__reset {
      padding: 0;
      font-size: 11px;
      font-weight: 500;
      color: var(--color-primary-600);
      cursor: pointer;
      background: none;
      border: none;

      &:hover {
        color: var(--color-primary-700);
        text-decoration: underline;
      }
    }

    &__option {
      display: flex;
      gap: 8px;
      align-items: center;
      padding: 6px 4px;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: var(--slate-50);
      }

      input[type='checkbox'] {
        width: 16px;
        height: 16px;
        cursor: pointer;
        accent-color: var(--color-primary-600);
      }

      .submission-status {
        font-size: 13px;
      }

      .form-name {
        font-size: 13px;
        color: var(--slate-700);
      }
    }

    &__empty {
      padding: 8px 4px;
      font-size: 12px;
      color: var(--slate-500);
      text-align: center;
    }
  }
</style>
